import {
  DataGrid,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridFooterContainer,
  GridPagination,
  GridPaginationModel,
  GridRowSelectionModel,
  gridPaginationModelSelector,
  gridRowCountSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import { Box, Typography } from '@mui/material';
import { DeletedItemDoc, EntityType } from '../../types/softDelete.types';

function extractName(item: DeletedItemDoc): string {
  const s = item.snapshot;
  switch (item.entityType as EntityType) {
    case 'contact':
    case 'client':
    case 'agreement':
      return (s.name as string) ?? '—';
    case 'meeting':
      return (s.title as string) ?? new Date((s.start as any)?.date ?? '').toLocaleDateString();
    case 'file_note':
      return (s.fileDescription as string) ?? (s.fileName as string) ?? '—';
    case 'fact_find':
      return `Fact Find — ${s.clientId ?? ''}`;
    case 'note':
      return (s.fileName as string) ?? (s.fileDescription as string) ?? '—';
    case 'file_upload':
      return (s.fileName as string) ?? '—';
    case 'signature_envelope':
      return (s.title as string) ?? '—';
    case 'drive_file':
      return (s.name as string) ?? '—';
    default:
      return '—';
  }
}

function formatEntityLabel(entityType: string): string {
  return entityType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Matches Homepage meetings DataGrid footer (“Showing X to Y of Z” + pagination). */
function RecycleBinTableFooter() {
  const apiRef = useGridApiContext();
  const paginationModel = useGridSelector(apiRef, gridPaginationModelSelector);
  const rowCount = useGridSelector(apiRef, gridRowCountSelector);
  const from = rowCount === 0 ? 0 : paginationModel.page * paginationModel.pageSize + 1;
  const to = Math.min((paginationModel.page + 1) * paginationModel.pageSize, rowCount);
  return (
    <GridFooterContainer
      sx={{
        px: 2,
        borderTop: '1px solid #e5e7eb',
        backgroundColor: 'rgba(249,250,251,0.5)',
        '& .MuiTablePagination-selectLabel': { mb: 0 },
        '& .MuiTablePagination-displayedRows': { mb: 0 },
        '& .MuiTablePagination-select': {
          paddingTop: 0,
          paddingBottom: 0,
          lineHeight: '1.5',
        },
      }}
    >
      <Typography sx={{ color: '#6b7280', alignSelf: 'center' }}>
        Showing{' '}
        <strong style={{ color: '#111827' }}>{from}</strong> to{' '}
        <strong style={{ color: '#111827' }}>{to}</strong> of{' '}
        <strong style={{ color: '#111827' }}>{rowCount}</strong> results
      </Typography>
      <GridPagination />
    </GridFooterContainer>
  );
}

const meetingsTableSx = {
  '& .MuiDataGrid-row': { cursor: 'default' },
  border: 'none',
  overflow: 'hidden',
  width: '100%',
  '& .MuiDataGrid-virtualScroller': {
    overflowY: 'auto',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: 'rgba(249, 250, 251, 0.8)',
    borderBottom: '1px solid #e5e7eb',
    '& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeaderCheckbox)': {
      px: '16px',
      py: '12px',
    },
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    textTransform: 'uppercase',
    fontSize: '0.7rem',
    letterSpacing: '0.05em',
    fontWeight: 600,
    color: '#6b7280',
  },
  '& .MuiDataGrid-cell:not(.MuiDataGrid-cellCheckbox)': {
    px: '16px',
    py: '12px',
  },
  '& .MuiDataGrid-cellCheckbox': {
    py: '12px',
    pl: 0.5,
    pr: 1.5,
    justifyContent: 'center',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'rgba(249, 250, 251, 0.8)',
  },
  '& .MuiDataGrid-footerContainer': {
    overflow: 'hidden',
  },
  '& .MuiTablePagination-displayedRows': {
    display: 'none',
  },
  '& .MuiDataGrid-columnHeaderCheckbox': {
    py: '12px',
    pl: 0.5,
    pr: 1.5,
    justifyContent: 'center',
  },
};

interface Props {
  rows: DeletedItemDoc[];
  total: number;
  page: number;
  pageSize: number;
  loading: boolean;
  rowSelectionModel: GridRowSelectionModel;
  onRowSelectionModelChange: (model: GridRowSelectionModel) => void;
  onPaginationModelChange: (model: GridPaginationModel) => void;
}

const buildColumns = (): GridColDef[] => [
  {
    field: 'deletedAt',
    headerName: 'Deleted At',
    minWidth: 170,
    flex: 0.9,
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827' }}>
          {new Date(row.deletedAt).toLocaleString()}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'entityType',
    headerName: 'Entity',
    minWidth: 120,
    flex: 0.7,
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Typography sx={{ fontSize: '0.875rem', color: '#4b5563' }}>
          {formatEntityLabel(row.entityType)}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 1.2,
    minWidth: 140,
    align: 'left',
    headerAlign: 'left',
    sortable: false,
    valueGetter: (_value, row) => extractName(row as DeletedItemDoc),
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', maxWidth: '100%' }}>
        <Typography
          sx={{
            fontWeight: 500,
            color: '#111827',
            fontSize: '0.875rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {extractName(row as DeletedItemDoc)}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'clientName',
    headerName: 'Client',
    minWidth: 140,
    flex: 1,
    align: 'left',
    headerAlign: 'left',
    sortable: false,
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', maxWidth: '100%' }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: '#4b5563',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.clientName ?? '—'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'orgId',
    headerName: 'Org',
    minWidth: 180,
    flex: 1,
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', maxWidth: '100%' }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: '#6b7280',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.orgName ?? row.orgId}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'deletedBy',
    headerName: 'Deleted By',
    minWidth: 160,
    flex: 0.9,
    align: 'left',
    headerAlign: 'left',
    renderCell: ({ row }) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', maxWidth: '100%' }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: '#6b7280',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.deletedBy ?? '—'}
        </Typography>
      </Box>
    ),
  },
  {
    ...GRID_CHECKBOX_SELECTION_COL_DEF,
    flex: 0,
    width: 56,
    minWidth: 56,
    maxWidth: 56,
  },
];

export default function DeletedItemsTable({
  rows,
  total,
  page,
  pageSize,
  loading,
  rowSelectionModel,
  onRowSelectionModelChange,
  onPaginationModelChange,
}: Props) {
  return (
    <Box sx={{ flexGrow: 1, width: '100%', overflow: 'hidden', minHeight: 0 }}>
      <DataGrid
        rows={rows}
        columns={buildColumns()}
        getRowId={(r) => r._id}
        rowCount={total}
        paginationMode="server"
        paginationModel={{ page: page - 1, pageSize }}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50]}
        loading={loading}
        checkboxSelection
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={onRowSelectionModelChange}
        disableRowSelectionOnClick
        rowHeight={48}
        slots={{ footer: RecycleBinTableFooter }}
        hideFooterSelectedRowCount
        sx={{
          ...meetingsTableSx,
          height: '100%',
          minHeight: { xs: 420, lg: 'calc(100vh - 320px)' },
        }}
      />
    </Box>
  );
}
